import { useState } from "react";
import type {
  ApprovalMode,
  ApprovalStepVerb,
  ApproverType,
  TimeoutAction,
  Workflow,
  WorkflowStep,
} from "../types";
import {
  useAddWorkflowStep,
  useDeleteWorkflowStep,
  useUpdateWorkflowStep,
  useWorkflowOptions,
} from "../hooks";
import { approvalModes, approverTypes, label, stepVerbs, timeoutActions } from "../utils";
import { useToast } from "#/components/common/ToastProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";

export function WorkflowStepBuilder({ workflow }: { workflow: Workflow }) {
  const addStep = useAddWorkflowStep();
  const updateStep = useUpdateWorkflowStep();
  const deleteStep = useDeleteWorkflowStep();
  const { data: options } = useWorkflowOptions();
  const toast = useToast();
  const confirm = useConfirm();

  const [editingId, setEditingId] = useState<number | null>(null);

  const [form, setForm] = useState({
    stepOrder: (workflow.steps?.length ?? 0) + 1,
    stepName: "",
    approverType: "APPLICANT_DEPARTMENT_HEAD" as ApproverType,
    roleId: "",
    specificUserId: "",
    allowDelegation: true,
    autoSkipIfApplicantIsApprover: true,
    allowSelfApproval: false,
    requiredStep: false,
    skipIfNoApproverFound: false,
    active: true,
    slaHours: "",
    escalationUserId: "",
    escalationRoleId: "",
    timeoutAction: "DO_NOTHING" as TimeoutAction,
    approvalMode: "SINGLE_APPROVER" as ApprovalMode,
    verb: "APPROVE" as ApprovalStepVerb,
  });

  function resetForm() {
    setEditingId(null);
    setForm({
      stepOrder: (workflow.steps?.length ?? 0) + 1,
      stepName: "",
      approverType: "APPLICANT_DEPARTMENT_HEAD",
      roleId: "",
      specificUserId: "",
      allowDelegation: true,
      autoSkipIfApplicantIsApprover: true,
      allowSelfApproval: false,
      requiredStep: false,
      skipIfNoApproverFound: false,
      active: true,
      slaHours: "",
      escalationUserId: "",
      escalationRoleId: "",
      timeoutAction: "DO_NOTHING",
      approvalMode: "SINGLE_APPROVER",
      verb: "APPROVE",
    });
  }

  function toPayload() {
    return {
      stepOrder: Number(form.stepOrder),
      stepName: form.stepName.trim(),
      approverType: form.approverType,
      roleId:
        form.approverType === "ROLE_BASED_APPROVER" && form.roleId
          ? Number(form.roleId)
          : null,
      specificUserId:
        form.approverType === "SPECIFIC_USER" && form.specificUserId
          ? Number(form.specificUserId)
          : null,
      allowDelegation: form.allowDelegation,
      autoSkipIfApplicantIsApprover: form.autoSkipIfApplicantIsApprover,
      allowSelfApproval: form.allowSelfApproval,
      requiredStep: form.requiredStep,
      skipIfNoApproverFound: form.skipIfNoApproverFound,
      slaHours: form.slaHours ? Number(form.slaHours) : null,
      timeoutAction: form.timeoutAction,
      escalationUserId:
        form.timeoutAction === "ESCALATE_TO_ANOTHER_APPROVER" &&
        form.escalationUserId
          ? Number(form.escalationUserId)
          : null,
      escalationRoleId:
        form.timeoutAction === "ESCALATE_TO_ANOTHER_APPROVER" &&
        form.escalationRoleId
          ? Number(form.escalationRoleId)
          : null,
      approvalMode: form.approvalMode,
      active: form.active,
      verb: form.verb,
    };
  }

  async function saveStep() {
    if (!form.stepName.trim()) {
      toast.error("Step name is required.");
      return;
    }

    if (form.approverType === "ROLE_BASED_APPROVER" && !form.roleId) {
      toast.error("Please select or enter the role ID for this approval step.");
      return;
    }

    if (form.approverType === "SPECIFIC_USER" && !form.specificUserId) {
      toast.error("Please select or enter the user ID for this approval step.");
      return;
    }

    if (
      form.timeoutAction === "ESCALATE_TO_ANOTHER_APPROVER" &&
      !form.escalationUserId &&
      !form.escalationRoleId
    ) {
      toast.error("Please provide an escalation user ID or escalation role ID.");
      return;
    }

    const wasPublished = workflow.published;

    if (editingId) {
      await updateStep.mutateAsync({
        workflowId: workflow.id,
        stepId: editingId,
        payload: toPayload(),
      });
    } else {
      await addStep.mutateAsync({
        workflowId: workflow.id,
        payload: toPayload(),
      });
    }

    if (wasPublished) {
      toast.info("This unpublished the workflow — new requests won't route through it until you publish it again.");
    }

    resetForm();
  }

  function edit(step: WorkflowStep) {
    setEditingId(step.id);
    setForm({
      stepOrder: step.stepOrder,
      stepName: step.stepName,
      approverType: step.approverType,
      roleId: step.roleId?.toString() ?? "",
      specificUserId: step.specificUserId?.toString() ?? "",
      allowDelegation: step.allowDelegation,
      autoSkipIfApplicantIsApprover: step.autoSkipIfApplicantIsApprover,
      allowSelfApproval: step.allowSelfApproval,
      requiredStep: step.requiredStep ?? false,
      skipIfNoApproverFound: step.skipIfNoApproverFound ?? false,
      active: step.active,
      slaHours: step.slaHours?.toString() ?? "",
      escalationUserId: step.escalationUserId?.toString() ?? "",
      escalationRoleId: step.escalationRoleId?.toString() ?? "",
      timeoutAction: step.timeoutAction ?? "DO_NOTHING",
      approvalMode: step.approvalMode ?? "SINGLE_APPROVER",
      verb: step.verb ?? "APPROVE",
    });
  }

  async function remove(stepId: number) {
    if (!(await confirm("Delete this workflow step?"))) return;

    const wasPublished = workflow.published;

    await deleteStep.mutateAsync({
      workflowId: workflow.id,
      stepId,
    });

    if (wasPublished) {
      toast.info("This unpublished the workflow — new requests won't route through it until you publish it again.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            Approval Steps
          </h3>
          <p className="text-sm text-slate-500">
            Add the approval stages in order. The system will resolve department heads, roles, delegations, and escalation automatically.
          </p>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <Field label="Step order">
            <input
              type="number"
              min={1}
              value={form.stepOrder}
              onChange={(e) =>
                setForm({ ...form, stepOrder: Number(e.target.value) })
              }
              className="input"
            />
          </Field>

          <Field label="Step name">
            <input
              value={form.stepName}
              onChange={(e) => setForm({ ...form, stepName: e.target.value })}
              className="input"
              placeholder="Department Head Review"
            />
          </Field>

          <Field label="Who should approve?">
            <select
              value={form.approverType}
              onChange={(e) =>
                setForm({
                  ...form,
                  approverType: e.target.value as ApproverType,
                  roleId: "",
                  specificUserId: "",
                })
              }
              className="input"
            >
              {approverTypes.map((x) => (
                <option key={x} value={x}>
                  {label(x)}
                </option>
              ))}
            </select>
          </Field>

          {form.approverType === "ROLE_BASED_APPROVER" && (
            <Field label="Approval role">
              <select
                value={form.roleId}
                onChange={(e) => setForm({ ...form, roleId: e.target.value })}
                className="input"
              >
                <option value="">— Select role —</option>
                {options?.roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name ?? r.code ?? r.id}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {form.approverType === "SPECIFIC_USER" && (
            <Field label="Approver">
              <select
                value={form.specificUserId}
                onChange={(e) =>
                  setForm({ ...form, specificUserId: e.target.value })
                }
                className="input"
              >
                <option value="">— Select user —</option>
                {options?.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName ?? u.username ?? u.id}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Approval mode">
            <select
              value={form.approvalMode}
              onChange={(e) =>
                setForm({
                  ...form,
                  approvalMode: e.target.value as ApprovalMode,
                })
              }
              className="input"
            >
              {approvalModes.map((x) => (
                <option key={x} value={x}>
                  {label(x)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Action wording">
            <select
              value={form.verb}
              onChange={(e) =>
                setForm({ ...form, verb: e.target.value as ApprovalStepVerb })
              }
              className="input"
            >
              {stepVerbs.map((x) => (
                <option key={x} value={x}>
                  {label(x)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="SLA hours">
            <input
              type="number"
              min={1}
              value={form.slaHours}
              onChange={(e) => setForm({ ...form, slaHours: e.target.value })}
              className="input"
              placeholder="Optional e.g. 24"
            />
          </Field>

          <Field label="Timeout action">
            <select
              value={form.timeoutAction}
              onChange={(e) =>
                setForm({
                  ...form,
                  timeoutAction: e.target.value as TimeoutAction,
                  escalationUserId: "",
                  escalationRoleId: "",
                })
              }
              className="input"
            >
              {timeoutActions.map((x) => (
                <option key={x} value={x}>
                  {label(x)}
                </option>
              ))}
            </select>
          </Field>

          {form.timeoutAction === "ESCALATE_TO_ANOTHER_APPROVER" && (
            <>
              <Field label="Escalation user">
                <select
                  value={form.escalationUserId}
                  onChange={(e) =>
                    setForm({ ...form, escalationUserId: e.target.value })
                  }
                  className="input"
                >
                  <option value="">— Select user (optional) —</option>
                  {options?.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName ?? u.username ?? u.id}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Escalation role">
                <select
                  value={form.escalationRoleId}
                  onChange={(e) =>
                    setForm({ ...form, escalationRoleId: e.target.value })
                  }
                  className="input"
                >
                  <option value="">— Select role (optional) —</option>
                  {options?.roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name ?? r.code ?? r.id}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          <Check
            label="Allow delegation"
            description="Approver can delegate to another user."
            checked={form.allowDelegation}
            onChange={(v) => setForm({ ...form, allowDelegation: v })}
          />

          <Check
            label="Auto-skip if applicant is approver"
            description="Step is skipped when the applying employee is also the approver for this step."
            checked={form.autoSkipIfApplicantIsApprover}
            onChange={(v) => setForm({ ...form, autoSkipIfApplicantIsApprover: v })}
          />

          <Check
            label="Skip if no approver found"
            description="Step is silently skipped when no eligible approver can be resolved at runtime (e.g. vacant role)."
            checked={form.skipIfNoApproverFound}
            onChange={(v) => setForm({ ...form, skipIfNoApproverFound: v })}
          />

          <Check
            label="Required step"
            description="This step must be completed — it will never be auto-skipped, even if the applicant is the approver."
            checked={form.requiredStep}
            onChange={(v) => setForm({ ...form, requiredStep: v })}
          />

          <Check
            label="Allow self approval"
            description="Applicant may approve their own request at this step."
            checked={form.allowSelfApproval}
            onChange={(v) => setForm({ ...form, allowSelfApproval: v })}
          />

          <Check
            label="Active"
            description="Inactive steps are ignored during workflow execution."
            checked={form.active}
            onChange={(v) => setForm({ ...form, active: v })}
          />
        </div>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={saveStep}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
          >
            {editingId ? "Update Step" : "Add Step"}
          </button>

          {editingId ? (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border px-4 py-2 text-sm font-semibold"
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        {[...(workflow.steps ?? [])]
          .sort((a, b) => a.stepOrder - b.stepOrder)
          .map((step) => (
            <div
              key={step.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700">
                      {step.stepOrder}
                    </span>

                    <div>
                      <h4 className="font-bold text-slate-900">
                        {step.stepName}
                      </h4>
                      <p className="text-xs text-slate-500">
                        {label(step.approverType)}
                        {step.roleCode ? ` · ${step.roleCode}` : ""}
                        {step.specificUsername ? ` · ${step.specificUsername}` : ""}
                        {" • "}
                        {label(step.approvalMode ?? "SINGLE_APPROVER")}
                        {" • "}
                        {step.verbActionLabel ?? label(step.verb ?? "APPROVE")} / {step.verbPastLabel ?? label(step.verb ?? "APPROVE")}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {step.allowDelegation ? <Badge>Delegation Allowed</Badge> : null}
                    {step.autoSkipIfApplicantIsApprover ? <Badge color="amber">Auto-Skip Applicant</Badge> : null}
                    {step.skipIfNoApproverFound ? <Badge color="blue">Skip If No Approver</Badge> : null}
                    {step.requiredStep ? <Badge color="red">Required</Badge> : null}
                    {step.slaHours ? <Badge>SLA {step.slaHours}h</Badge> : null}
                    {step.timeoutAction && step.timeoutAction !== "DO_NOTHING" ? <Badge>{label(step.timeoutAction)}</Badge> : null}
                    {step.active ? <Badge>Active</Badge> : <Badge color="red">Inactive</Badge>}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => edit(step)}
                    className="rounded-lg border px-3 py-1 text-sm"
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => remove(step.id)}
                    className="rounded-lg bg-red-50 px-3 py-1 text-sm text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

        {!workflow.steps?.length ? (
          <div className="rounded-2xl border bg-white p-6 text-center text-sm text-slate-500">
            No approval steps configured yet.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-1 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Check({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 text-sm hover:bg-slate-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0"
      />
      <div>
        <div className="font-semibold text-slate-700">{label}</div>
        {description && <div className="mt-0.5 text-xs text-slate-400">{description}</div>}
      </div>
    </label>
  );
}

const badgeColors = {
  default: "bg-slate-100 text-slate-600",
  amber:   "bg-amber-100 text-amber-700",
  blue:    "bg-blue-100 text-blue-700",
  red:     "bg-red-100 text-red-700",
};

function Badge({ children, color = "default" }: { children: React.ReactNode; color?: keyof typeof badgeColors }) {
  return (
    <span className={`rounded-full px-2 py-1 font-medium ${badgeColors[color]}`}>
      {children}
    </span>
  );
}